/**
 * Storefront / public merchant gallery: merge merchant-owned media with live Google Places photos.
 * See docs/CONTEXT-merchant-media.md (platform contract).
 */

import type { MerchantProfile } from '@/types/api/merchant';

export type StorefrontGalleryImage = NonNullable<MerchantProfile['images']>[number];

function isGoogleMapsSource(source: string | undefined): boolean {
  return String(source ?? '')
    .toLowerCase()
    .replace(/\s+/g, '') === 'google-maps';
}

/** photo_reference / photoreference query param when present */
export function extractPhotoReferenceFromUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const r =
      u.searchParams.get('photo_reference') ||
      u.searchParams.get('photoreference') ||
      u.searchParams.get('photoReference');
    const t = r?.trim();
    return t || null;
  } catch {
    return null;
  }
}

export function normalizeMediaUrlForDedupe(url: string): string {
  const t = url.trim();
  if (!t) return '';
  const fromQuery = extractPhotoReferenceFromUrl(t);
  if (fromQuery) return `photo_ref:${fromQuery}`;
  try {
    const u = new URL(t);
    u.hash = '';
    const entries = [...u.searchParams.entries()].sort(([a], [b]) => a.localeCompare(b));
    const q = entries.map(([k, v]) => `${k}=${v}`).join('&');
    return `${u.origin}${u.pathname}${q ? `?${q}` : ''}`;
  } catch {
    return t.toLowerCase();
  }
}

export type LivePlacePhoto = { url: string; photoRef?: string };

export function galleryDedupeKeyForExplicitImage(img: StorefrontGalleryImage): string {
  const fromMeta = img.metadata?.photoReference?.trim();
  if (fromMeta) return `photo_ref:${fromMeta}`;
  const fromUrl = extractPhotoReferenceFromUrl(img.url);
  if (fromUrl) return `photo_ref:${fromUrl}`;
  return normalizeMediaUrlForDedupe(img.url);
}

export function galleryDedupeKeyForLivePhoto(p: LivePlacePhoto): string {
  const r = p.photoRef?.trim();
  if (r) return `photo_ref:${r}`;
  return normalizeMediaUrlForDedupe(p.url);
}

/**
 * Merchant-owned gallery: uploads / GCS / non–Google Places API rows.
 * When `forLiveGoogleAugmentation` is true, rows with source `google-maps` are excluded so live Places photos can replace stale API Google URLs.
 */
export function buildExplicitMerchantImages(
  merchant: MerchantProfile,
  options?: { forLiveGoogleAugmentation?: boolean }
): StorefrontGalleryImage[] {
  const stripApiGoogle = options?.forLiveGoogleAugmentation === true;

  const fromRows = (merchant.images || []).filter((img) => {
    if (!img?.url?.trim()) return false;
    if (stripApiGoogle && isGoogleMapsSource(img.source)) return false;
    return true;
  });

  if (fromRows.length > 0) {
    return [...fromRows].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  return (merchant.photos || [])
    .map((url, i) => ({
      id: `legacy-photo-${i}`,
      url: typeof url === 'string' ? url : String((url as { url?: string })?.url || ''),
      caption: '',
      isPrimary: i === 0,
      order: i,
    }))
    .filter((img) => img.url.length > 0);
}

/**
 * Required merge order: [ ...explicitMerchantImages, ...liveGoogleImages ], with dedupe.
 */
export function mergeExplicitAndLiveGooglePhotos(
  explicit: StorefrontGalleryImage[],
  live: LivePlacePhoto[],
  placeId: string
): StorefrontGalleryImage[] {
  const seen = new Set<string>();
  const out: StorefrontGalleryImage[] = [];

  for (const img of explicit) {
    const k = galleryDedupeKeyForExplicitImage(img);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(img);
  }

  let liveIdx = 0;
  for (const p of live) {
    const k = galleryDedupeKeyForLivePhoto(p);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push({
      id: `places-live-${placeId}-${liveIdx}`,
      url: p.url,
      caption: '',
      isPrimary: false,
      order: 1000 + liveIdx,
      source: 'google-maps',
    });
    liveIdx += 1;
  }

  return out.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function isDurableStoredUrl(url: string): boolean {
  return (
    url.includes('storage.googleapis.com') || url.includes('storage.cloud.google.com')
  );
}

function isGooglePlacePhotoHttpUrl(url: string): boolean {
  return url.includes('maps.googleapis.com/maps/api/place/photo');
}

function isDisplayableStorefrontPhotoUrl(url: string, source?: string): boolean {
  const t = url.trim();
  if (!t) return false;
  if (isDurableStoredUrl(t)) return true;
  if (isGooglePlacePhotoHttpUrl(t)) return false;
  if (t.includes('/api/v1/public/merchant/photo')) return false;
  if (isGoogleMapsSource(source)) return false;
  return true;
}

/** True when the profile already has at least one photo URL safe to render without backend hydration. */
export function merchantHasDisplayablePhotos(merchant: MerchantProfile | null): boolean {
  if (!merchant) return false;

  const logo = merchant.logo?.trim();
  if (logo && isDisplayableStorefrontPhotoUrl(logo)) return true;

  return buildExplicitMerchantImages(merchant).some((img) =>
    isDisplayableStorefrontPhotoUrl(img.url, img.source)
  );
}

/**
 * Platform lazy-hydrates Google Place photos after GET /public/merchant/:id.
 * Poll the profile until displayable URLs appear (see pay merchantPlacePhotoHydrationService).
 */
export function merchantProfileAwaitingPhotoHydration(
  merchant: MerchantProfile | null
): boolean {
  if (!merchant?.googlePlaceId?.trim()) return false;
  return !merchantHasDisplayablePhotos(merchant);
}
